import type * as React from 'react';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import { signInWithGoogle } from '@/auth/firebaseAuth';
import { useAuth } from '@/auth/AuthProvider';
import { useColorScheme } from '@/lib/theme';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { RestrictedAccess } from './RestrictedAccess';

const TAG = 'admin/login';

type LoginError =
  | 'errorPopupBlocked'
  | 'errorAccountExists'
  | 'errorGeneric';

/** Traduz o `code` do Firebase Auth na chave i18n de erro do login. */
function classifyError(err: unknown): LoginError {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = String((err as { code: unknown }).code);
    if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user') {
      return 'errorPopupBlocked';
    }
    if (code === 'auth/account-exists-with-different-credential') {
      return 'errorAccountExists';
    }
  }
  return 'errorGeneric';
}

/**
 * Tela `/login` do admin: único botão "Entrar com Google" (popup).
 *
 * Origem: sem issue — commit 88be39195, 2026-04-26 (Sprint 2, Google SSO
 * restrito + guard de superAdmin).
 *
 * Contrato: com `status` AUTHORIZED, redireciona para `location.state.from`
 * (gravado pelo `RequireSuperAdmin`) ou `/`. Com variante de recusa
 * (WRONG_DOMAIN/EMAIL_NOT_VERIFIED/MISSING_CLAIM), mostra `RestrictedAccess`.
 * O clique chama `signInWithGoogle()`; erro vira mensagem i18n por
 * `classifyError` (popup bloqueado, conta com outra credencial, genérico).
 *
 * Usado por: rota `login` (`routes/router.tsx`) — a única pública; o
 * `RequireSuperAdmin` manda para cá quem está deslogado.
 *
 * Arquitetura: quem decide o acesso é o `AuthProvider` (`authStatus.ts`: domínio
 * `@etiquetachef.com`, e-mail verificado, claim `superAdmin`); esta tela só
 * reage ao `status`. O dado é protegido pelas rules/callables, não por ela.
 *
 * Dicas: o erro de login só vai para `console.error` — popup fechado pelo
 * operador não é falha que valha evento.
 */
export function Login(): React.JSX.Element {
  const { t } = useTranslation();
  const { status } = useAuth();
  const location = useLocation();
  // Logo 3:2 (PNG da arte oficial). No modo escuro a variante tem "Etiqueta"
  // em branco; no claro, azul-marinho. Ambas com fundo transparente.
  const isDark = useColorScheme() === 'dark';
  const logoSrc = isDark ? '/brand/logo-dark.png' : '/brand/logo.png';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);

  if (status.kind === 'AUTHORIZED') {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }
  if (
    status.kind === 'WRONG_DOMAIN' ||
    status.kind === 'EMAIL_NOT_VERIFIED' ||
    status.kind === 'MISSING_CLAIM'
  ) {
    return <RestrictedAccess status={status} />;
  }

  async function handleSignIn(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await signInWithGoogle();
      console.info(`${TAG}: popup resolved`);
    } catch (err) {
      console.error(`${TAG}: signInWithGoogle failed`, err);
      const key = classifyError(err);
      // #1021 (2026-10-23): só o genérico (config, rede, provedor) é reportado; popup
      // bloqueado/fechado e conta com outra credencial são fluxos normais. Sem e-mail (PII).
      if (key === 'errorGeneric') {
        reportNonFatal('admin_login_failed', {
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
      }
      setError(key);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: '0 16px' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          maxWidth: 384,
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
          textAlign: 'center',
        }}
      >
        {/* Linha 1: logo (adapta claro/escuro). Linha 2: botão. */}
        <img src={logoSrc} alt="EtiquetaChef" style={{ height: 128, width: 'auto' }} />

        <Button
          variant="primary"
          loading={submitting}
          onClick={(): void => {
            void handleSignIn();
          }}
        >
          {t('auth.login.signInWithGoogle')}
        </Button>

        {error && (
          <Box color="text-status-error" textAlign="center">
            {t(`auth.login.${error}`)}
          </Box>
        )}
      </div>
    </main>
  );
}
